// src/components/complaints/SubmitComplaint.js
import React, { useState } from 'react';
import { supabase } from '../../config/supabase.config';
import '../../pages/UserDashboard.css';
import './ComplaintManagement.css';

function SubmitComplaint({ userData, onBack, onSubmit }) {
  const [formData, setFormData] = useState({
    category: 'maintenance',
    subject: '',
    description: '',
    location: '',
    urgency: 'normal'
  });
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');

  const categories = [
    { value: 'maintenance', label: 'Maintenance', icon: '🔧' },
    { value: 'cleanliness', label: 'Cleanliness', icon: '🧹' },
    { value: 'security', label: 'Security', icon: '🔒' },
    { value: 'noise', label: 'Noise', icon: '🔇' },
    { value: 'parking', label: 'Parking', icon: '🚗' },
    { value: 'amenities', label: 'Amenities', icon: '🏊' },
    { value: 'other', label: 'Other', icon: '📌' }
  ];

  const urgencyLevels = [
    { value: 'urgent', label: 'Urgent - Immediate attention', color: '#dc3545', icon: '🔴' },
    { value: 'high', label: 'High - Within 24 hours', color: '#ffc107', icon: '🟡' },
    { value: 'normal', label: 'Normal - Within 2-3 days', color: '#28a745', icon: '🟢' },
    { value: 'low', label: 'Low - General improvement', color: '#17a2b8', icon: '🔵' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const complaintNumber = `CMP${Date.now().toString().slice(-8)}`;

      const { error } = await supabase
        .from('complaints')
        .insert([{
          complaint_number: complaintNumber,
          ...formData,
          created_by: userData.id,
          status: 'pending',
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      alert('Complaint submitted successfully!');
      onSubmit();
    } catch (error) {
      console.error('Error submitting complaint:', error);
      alert('Failed to submit complaint');
    } finally {
      setLoading(false);
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className={`dashboard-container complaint-submit-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">📋</span>
            <span className="menu-label">All Complaints</span>
          </button>
          
          <button
            className="menu-item active"
          >
            <span className="menu-icon">➕</span>
            <span className="menu-label">New Complaint</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <div className="info-section">
            <h4>Quick Guidelines</h4>
            <ul className="guidelines-list">
              <li>Be specific about the issue</li>
              <li>Include location details</li>
              <li>Select appropriate urgency</li>
              <li>Provide clear description</li>
            </ul>
          </div>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">New Complaint</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="complaint-header">
            <h2 className="page-title">Submit New Complaint</h2>
          </div>

          <div className="dashboard-card">
            <form onSubmit={handleSubmit} className="complaint-form">
              {/* Category Selection */}
              <div className="form-group">
                <label className="form-label">
                  Category <span className="required">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="form-select"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div className="form-group">
                <label className="form-label">
                  Subject <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="form-input"
                  placeholder="Brief description of the issue"
                  required
                />
              </div>

              {/* Description */}
              <div className="form-group">
                <label className="form-label">
                  Detailed Description <span className="required">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="form-textarea"
                  placeholder="Provide detailed information about the complaint"
                  rows="5"
                  required
                />
              </div>

              {/* Location */}
              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Lobby, Parking Area, Flat 101"
                />
              </div>

              {/* Urgency Level */}
              <div className="form-group">
                <label className="form-label">
                  Urgency Level <span className="required">*</span>
                </label>
                <div className="urgency-options">
                  {urgencyLevels.map(level => (
                    <label
                      key={level.value}
                      className={`urgency-option ${formData.urgency === level.value ? 'selected' : ''}`}
                      style={{
                        borderColor: formData.urgency === level.value ? level.color : 'var(--border-color)',
                        background: formData.urgency === level.value ? `${level.color}10` : 'var(--bg-primary)'
                      }}
                    >
                      <input
                        type="radio"
                        name="urgency"
                        value={level.value}
                        checked={formData.urgency === level.value}
                        onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                        className="urgency-radio"
                      />
                      <span className="urgency-content">
                        <span className="urgency-icon">{level.icon}</span>
                        <span className="urgency-label" style={{ color: level.color }}>
                          {level.label}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary btn-large"
                >
                  {loading ? 'Submitting...' : 'Submit Complaint'}
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="btn-secondary btn-large"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SubmitComplaint;